// 会員情報機能で共通するお子さまに関する表示部分を定義
// 情報照会、変更内容確認で共有
//

import { Grid, Stack, styled } from '@mui/material'
import { ReactNode } from 'react'
import {
  getBloodTypeLabel,
  getCoveredLabel,
  getGenderLabel,
  getYesNoLabel,
} from '../../../../containers/common/codeMaster'
import { translate, translateNode } from '../../../../i18n'
import { ElapsedMillisecond, formatYmd } from '../../../../utils/dateUtil'
import { GItem, LabelGItemBase, ValueGItemBase } from '../grids'
import { SubTitle } from '../subTitle'
import { Child as ChildOriginal } from '../../../../containers/common/child'
import { OperationId } from '../../../../containers/common/constant/operationLog'
import { childUseEndUrl, memberModifyChildUrl } from '../../../../containers/common/constant/appUrl'
import { Link } from '../link'
import { GetCategoryMstDto } from '../../../../dataAccess/webApi/dto/categoryMstDto'
import { ChildAddressUnit } from './childAddressUnit'
import { ChildAllergyUnit } from './childAllergyUnit'
import { ChildFamilyHospitalUnit } from './childFamilyHospitalUnit'
import { ChildFebrileSeizureUnit } from './childFebrileSeizureUnit'
import { ChildHospitalAdmissionUnit } from './childHospitalAdmissionUnit'
import { ChildMedicalHistoryUnit } from './childMedicalHistoryUnit'
import { ChildNormallyUsedFacilityUnit } from './childNormallyUsedFacilityUnit'
import { ChildUsuallyMedicinesUnit } from './childUsuallyMedicinesUnit'
import { ChildDiseaseUnit } from './childDiseaseUnit'
import { ChildVaccinationUnit } from './childVaccinationUnit'
import { ChildEatingUnit } from './childEatingUnit'
import { ChildSymptomUnit } from './childSymptomUnit'
import { ChildPredispositionUnit } from './childPredispositionUnit'
import { ChildDiseaseTestUnit } from './childDiseaseTestUnit'
import { ChildTreatmentUnit } from './childTreatmentUnit'
import { UnitTitle } from './unitTitle'

interface Child {
  name: string
  kana: string
  birthday: Date | ElapsedMillisecond
  brothersSistersFlag: string
  multipleBirthsFlag: string
  gender: string
  bloodType?: string
  maternalHandbookNo: string | null
}

interface ChildReference extends Child {
  childId: string
  allergyFlag: string
  /** アレルギー１～３ */
  allergies: {
    causes?: string
    symptoms?: string
    treatments?: string
    concerns?: string
  }[]
}
/** 情報照会時以外で必要なプロパティ */
interface Confirmation {
  child: Child
  childOrig?: ChildOriginal
  displayType?: never
  /** アレルギー項目を非表示にする場合true */
  isHideAllergy?: boolean
  categoryMst?: GetCategoryMstDto[]
  categoryMap?: {[key:string]: GetCategoryMstDto}
  medicalHistoryCategory?: GetCategoryMstDto[]
  diseaseCategory?: GetCategoryMstDto[]
  vaccinationCategory?: GetCategoryMstDto[]
  symptomCategory?: GetCategoryMstDto[]
  modifyChildId?: never
  showModifyLink?: never
}
/** 情報照会時に必要なプロパティ */
interface Reference {
  child: ChildReference
  childOrig?: ChildOriginal
  /** 利用終了はこちらを表示する場合true */
  isShowUseEnd?: boolean
  displayType: 'reference'
  categoryMst?: GetCategoryMstDto[]
  categoryMap?: {[key:string]: GetCategoryMstDto}
  medicalHistoryCategory?: GetCategoryMstDto[]
  diseaseCategory?: GetCategoryMstDto[]
  vaccinationCategory?: GetCategoryMstDto[]
  symptomCategory?: GetCategoryMstDto[]
  modifyChildId?:string //ChildがchildIdを保持していないため、別途用意している
  showModifyLink?: boolean
}

type ChildProps = Confirmation | Reference
const LabelItem = (props: { children?: ReactNode }) => <LabelGItemBase xs={5}>{props.children}</LabelGItemBase>
const ValueItem = (props: { children: ReactNode }) => <ValueGItemBase xs={7}>{props.children}</ValueGItemBase>

const ChildContentAttribute = styled(Grid)({
  padding: '0 0 0 3rem',
})

/** 会員情報機能のお子さま情報コンテンツ部分 */
export const ChildContent = (props: ChildProps) => {
  const { categoryMap, child, childOrig, modifyChildId } = props
  const hideLink = props?.showModifyLink == null || !props.showModifyLink
  const unSetLabel = translate('system.childContent.label.notSet')
  
  return (
    <Stack spacing={2}>
      {/* 基本 */}
      <Stack spacing={1}>
        {modifyChildId 
          ? <UnitTitle title={translate('system.childContent.title.basic')} linkTo={memberModifyChildUrl.url(modifyChildId)} operationLog={{ operationId: OperationId.OP_00000057 }} hideLink={hideLink}/>
          : <SubTitle title={translate('system.childContent.title.basic')} />
        }
        <div>
          <ChildContentAttribute container>
            <LabelItem>{translate('system.childContent.label.childName')}</LabelItem>
            <ValueItem>{child.name}</ValueItem>
            <LabelItem>{translate('system.childContent.label.childNameKana')}</LabelItem>
            <ValueItem>{child.kana}</ValueItem>
            <LabelItem>{translate('system.childContent.label.birthday')}</LabelItem>
            <ValueItem>{formatYmd(child.birthday)}</ValueItem>
            {categoryMap?.brothersSistersFlag && <LabelItem>{categoryMap?.brothersSistersFlag.name}</LabelItem>}
            {categoryMap?.brothersSistersFlag && <ValueItem>{getYesNoLabel(child.brothersSistersFlag)}</ValueItem>}
            {categoryMap?.multipleBirthsFlag && <LabelItem>{categoryMap?.multipleBirthsFlag.name}</LabelItem>}
            {categoryMap?.multipleBirthsFlag && <ValueItem>{getCoveredLabel(child.multipleBirthsFlag)}</ValueItem>}
            {categoryMap?.childrenGender && <LabelItem>{categoryMap.childrenGender.name}</LabelItem>}
            {categoryMap?.childrenGender && <ValueItem>{getGenderLabel(child.gender)}</ValueItem>}
            {categoryMap?.childrenBloodType && <LabelItem>{categoryMap.childrenBloodType.name}</LabelItem>}
            {categoryMap?.childrenBloodType && <ValueItem>{getBloodTypeLabel(child.bloodType)}</ValueItem>}
            {categoryMap?.maternalHandbookNo && <LabelItem>{translate('system.childContent.label.maternalHandbookNo')}</LabelItem>}
            {categoryMap?.maternalHandbookNo && <ValueItem>{child.maternalHandbookNo}</ValueItem>}
          </ChildContentAttribute>
        </div>
      </Stack>
      
      {/* お子さまの住所・電話番号 */}
      {categoryMap?.childrenAddress &&
        <ChildAddressUnit
          sectionLabel={'お子さまの住所・電話番号'}
          child={childOrig}
          LabelItem={LabelItem}
          ValueItem={ValueItem}
        />
      }
      
      {/* 通常利用施設 */}
      <ChildNormallyUsedFacilityUnit
        sectionLabel={'通常利用施設'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      
      {/* かかりつけ医 */}
      <ChildFamilyHospitalUnit
        sectionLabel={'かかりつけ医'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      
      {/* 常時服用している薬 */}
      <ChildUsuallyMedicinesUnit
        sectionLabel={'常時服用している薬'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      
      {props.displayType === 'reference' ? (
        categoryMap?.allergyFlag && <Stack spacing={1}>
          <SubTitle title={translate(`system.childContent.title.allergy`)} />
          <div>
            <ChildContentAttribute container>
              <LabelItem>{categoryMap.allergyFlag.name}</LabelItem>
              <ValueItem>{getYesNoLabel(props.child.allergyFlag)}</ValueItem>
            </ChildContentAttribute>
          </div>
        </Stack>
      ) : (
        <>
          {!props.isHideAllergy && categoryMap?.allergy == null && (
            <Stack spacing={1}>
              <SubTitle title={translate(`system.childContent.title.allergy`)} />
              <div>
                <ChildContentAttribute>
                  <GItem xs={12}>{translate('system.userContent.text.exempt')}</GItem>
                </ChildContentAttribute>
              </div>
            </Stack>
          )}
        </>
      )}
      {/* アレルギー１～３ */}
      {props.displayType === 'reference' &&
        ['allergy1', 'allergy2', 'allergy3'].map((title, index) => {
          const allergy = props.child.allergies[index]
          const causes = categoryMap != null ? categoryMap[`allergyCauses${index + 1}`] : null
          const symptoms = categoryMap != null ? categoryMap[`allergySymptoms${index + 1}`] : null
          const treatments = categoryMap != null ? categoryMap[`allergyTreatments${index + 1}`] : null
          const concerns = categoryMap != null ? categoryMap[`allergyConcerns${index + 1}`] : null
          return (
            (causes || symptoms || treatments || concerns) &&
            <Stack key={title} spacing={1}>
              <SubTitle title={translate(`system.childContent.title.${title}`)} />
              <div>
                <ChildContentAttribute container>
                  {causes && <LabelItem>{causes.name}</LabelItem>}
                  {causes && <ValueItem>{allergy.causes || unSetLabel}</ValueItem>}
                  {symptoms && <LabelItem>{symptoms.name}</LabelItem>}
                  {symptoms && <ValueItem>{allergy.symptoms || unSetLabel}</ValueItem>}
                  {treatments && <LabelItem>{treatments.name}</LabelItem>}
                  {treatments && <ValueItem>{allergy.treatments || unSetLabel}</ValueItem>}
                  {concerns && <LabelItem>{concerns.name}</LabelItem>}
                  {concerns && <ValueItem>{allergy.concerns || unSetLabel}</ValueItem>}
                </ChildContentAttribute>
              </div>
            </Stack>
          )
        })}
        
      {/* アレルギー */}
      <ChildAllergyUnit
        sectionLabel={'アレルギー'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      
      {/* 熱性けいれん */}
      <ChildFebrileSeizureUnit
        sectionLabel={'熱性けいれん'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />

      {/* 既往症 */}
      <ChildMedicalHistoryUnit
        sectionLabel={'既往症'}
        child={childOrig}
        categoryMst={props.medicalHistoryCategory}
      />
      {/* 食事 */}
      <ChildEatingUnit
        sectionLabel={'食事'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      {/* 病気 */}
      <ChildDiseaseUnit
        sectionLabel={'病気'}
        child={childOrig}
        categoryMst={props.diseaseCategory}
      />
      {/* 症状 */}
      <ChildSymptomUnit
        sectionLabel={'症状'}
        child={childOrig}
        categoryMst={props.symptomCategory}
      />
      {/* 予防接種 */}
      <ChildVaccinationUnit
        sectionLabel={'予防接種'}
        child={childOrig}
        categoryMst={props.vaccinationCategory}
      />
      {/* 体質・くせ */}
      <ChildPredispositionUnit
        sectionLabel={'体質・くせ'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      {/* 入院歴 */}
      <ChildHospitalAdmissionUnit
        sectionLabel={'入院歴'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      {/* 検査歴 */}
      <ChildDiseaseTestUnit
        sectionLabel={'検査歴'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      {/* 治療歴 */}
      <ChildTreatmentUnit
        sectionLabel={'治療歴'}
        child={childOrig}
        categoryMst={props.categoryMst}
      />
      
      {/* そのほか */}
      {props.displayType === 'reference' && props.isShowUseEnd &&
        <Stack spacing={1}>
          <SubTitle title={translate('system.childContent.title.other')} />
          <div>
            <ChildContentAttribute container>
              <GItem xs={12}>
                {translateNode(
                  'system.childContent.text.useEnd',
                  <Link
                    to={childUseEndUrl.url(props.child.childId)}
                    operationLog={{ operationId: OperationId.OP_00000159 }}>
                    {translate('system.childContent.text.here')}
                  </Link>
                )}
              </GItem>
            </ChildContentAttribute>
          </div>
        </Stack>
      }
    </Stack>
  )
}
